import { initializeApp } from 'firebase/app';
import {
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  type User,
  type MultiFactorResolver,
  type MultiFactorError,
} from 'firebase/auth';
import {
  SHARE_TOKEN_KEY,
  decodeJwtPayload,
  extractShareObjectIdFromPath,
  isJwtNotExpired,
} from './share-token';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
};

// Must match scio1's app name so Firebase IndexedDB auth persists across dual-live switches.
// scio1: initializeApp(config, "___auth___") in authService.js
const FIREBASE_APP_NAME = '___auth___';

const app = initializeApp(firebaseConfig, FIREBASE_APP_NAME);
export const auth = getAuth(app);

// Resolves when Firebase determines initial auth state from IndexedDB.
// Registered at module-load time (before React renders) to capture
// the very first onAuthStateChanged callback. Prevents Apollo authLink
// from sending requests with empty Authorization headers during startup.
export const authInitialized = new Promise<void>((resolve) => {
  const unsubscribe = onAuthStateChanged(auth, () => {
    resolve();
    unsubscribe();
  });
});

// Skip reCAPTCHA challenge in local development only (staging/preview should still verify)
if (import.meta.env.VITE_DISABLE_RECAPTCHA === 'true') {
  auth.settings.appVerificationDisabledForTesting = true;
}

// Providers
export const googleProvider = new GoogleAuthProvider();
export const facebookProvider = new FacebookAuthProvider();
export const microsoftProvider = new OAuthProvider('microsoft.com');

// SAML provider factory
export function createSamlProvider(accountId: string) {
  return new SAMLAuthProvider(`saml.id_${accountId}`);
}

// Get fresh token with auto-refresh if expiring within 3 minutes
export async function getFreshToken(forceRefresh = false): Promise<{
  token: string;
  expires: string;
  uid: string;
}> {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');

  const tokenResult = await user.getIdTokenResult(forceRefresh);
  const expires = tokenResult.expirationTime;

  const diff = (new Date(expires).getTime() - Date.now()) / 60000;
  if (diff < 3) {
    const refreshed = await user.getIdTokenResult(true);
    return {
      token: refreshed.token,
      expires: refreshed.expirationTime,
      uid: user.uid,
    };
  }

  return { token: tokenResult.token, expires, uid: user.uid };
}

export {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signInWithCustomToken,
  signInWithEmailLink,
  isSignInWithEmailLink,
  sendPasswordResetEmail,
  confirmPasswordReset,
  applyActionCode,
  checkActionCode,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  GoogleAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  multiFactor,
  getMultiFactorResolver,
  TotpMultiFactorGenerator,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
};

// Secondary Auth instance for action code operations (applyActionCode, checkActionCode).
// Isolated from the main `auth` instance so that applying verification/reset codes
// doesn't trigger onAuthStateChanged listeners — prevents Safari ITP from causing
// a brief null-user state that triggers fullLogout().
//
// IMPORTANT: This instance does NOT need persistence — it's only used for one-shot
// applyActionCode/checkActionCode calls. We skip getAuth() entirely and use
// initializeAuth() with no persistence to avoid creating IndexedDB entries.
// A corrupted IndexedDB entry ("__action_code__!undefined") causes blank pages
// in Safari due to its strict IndexedDB handling.
let _actionAuth: ReturnType<typeof getAuth> | null = null;
export function getActionAuth() {
  if (!_actionAuth) {
    const actionApp = initializeApp(firebaseConfig, '__action_code__');
    _actionAuth = initializeAuth(actionApp, { persistence: inMemoryPersistence });
  }
  return _actionAuth;
}

// Convenience wrapper: extracts token string for auth callbacks (Apollo, DDP, etc.)
// Awaits authInitialized to prevent race condition where auth.currentUser is null
// during Firebase IndexedDB recovery, which would send requests without Bearer token.
//
// Share page priority: PREFER the cached share token (set by cacheShareToken after
// postAccessShareLink) over auth.currentUser.getIdToken(). Multiple signInAnonymously
// calls during a single page lifecycle (recovery race, ShareAssetsProvider mount,
// cross-tab Firebase IndexedDB sync) can replace auth.currentUser with a NEW anonymous
// user that lacks the shareLinkObjectId claim. Using its token would cause backend
// _getAuthFromDB to return NOT_FOUND_USER (line 172, auth.service.ts) on write mutations.
// The cached token always carries the claim from the most recent successful postAccess
// and is valid on Firebase's backend even after the client lost the user reference.
export async function getTokenString(): Promise<string | null> {
  await authInitialized;

  // Share link pages: cached share token is the source of truth.
  // Verify claim matches the CURRENT share link path — a stale cached token from a
  // previously-visited share link would carry the wrong shareLinkObjectId claim.
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/share/')) {
    const cached = _readCachedShareToken();
    if (cached && _isCachedShareTokenValidForCurrentPath(cached)) {
      return cached;
    }
  }

  try {
    const result = await getFreshToken();
    return result.token;
  } catch {
    // Last-resort fallback: stale cached token is better than no token (Apollo would
    // fail entirely without Authorization header). Backend will reject if truly invalid.
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/share/')) {
      const cached = _readCachedShareToken();
      if (cached) return cached;
    }
    return null;
  }
}

function _readCachedShareToken(): string | null {
  try {
    return sessionStorage.getItem(SHARE_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * True if the cached token is valid (not expired) AND, for anonymous users, carries
 * a `shareLinkObjectId` claim matching the current `/share/{type}/{objectId}` path.
 *
 * Without the claim-matches-path check, navigating from share A → share B in the same
 * tab would let the cache token from A leak into B's mutations, leading to backend
 * SHARE_LINK_ACCESS_DENIED on B's protected operations.
 */
function _isCachedShareTokenValidForCurrentPath(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || !isJwtNotExpired(payload)) return false;
  if (payload.firebase?.sign_in_provider !== 'anonymous') return true;
  // Reject stale anonymous cache when there is now a real signed-in user.
  // Scenario: ACCOUNT-type share (or any share where the user later authenticates) —
  // anonymous cached token from a prior visit must not override the real user's auth,
  // otherwise SmallApp REST `getMe()` resolves anonymous → no user → 403.
  if (auth.currentUser && !auth.currentUser.isAnonymous) return false;
  if (!payload.shareLinkObjectId) return false;
  const currentObjectId =
    typeof window !== 'undefined' ? extractShareObjectIdFromPath(window.location.pathname) : null;
  if (currentObjectId && payload.shareLinkObjectId !== currentObjectId) return false;
  return true;
}

export type { User, MultiFactorResolver, MultiFactorError };
